/**
 * Outreach History Component
 * 
 * Displays history of marketing campaigns with drill-down to recipients.
 */
import React, { useState, useEffect, useCallback } from "react";
import { 
  History, ChevronRight, Loader2, CheckCircle2, 
  XCircle, Clock, Mail, Users, AlertCircle
} from "lucide-react";
import { Card, CardContent } from "@/components/ui/card";
import { Button } from "@/components/ui/button";
import { Badge } from "@/components/ui/badge";
import {
  Dialog,
  DialogContent,
  DialogHeader,
  DialogTitle,
} from "@/components/ui/dialog";
import {
  Table,
  TableBody,
  TableCell,
  TableHead,
  TableHeader,
  TableRow,
} from "@/components/ui/table";
import { API_URL } from '@/config/api';

const OutreachHistory = () => {
  const [campaigns, setCampaigns] = useState([]);
  const [loading, setLoading] = useState(true);
  const [error, setError] = useState(null);
  const [total, setTotal] = useState(0);
  const [page, setPage] = useState(1);
  const [pageSize] = useState(20);
  const [hasMore, setHasMore] = useState(false);
  
  // Campaign detail modal
  const [selectedCampaign, setSelectedCampaign] = useState(null);
  const [detailLoading, setDetailLoading] = useState(false);
  const [campaignDetail, setCampaignDetail] = useState(null);

  const fetchHistory = useCallback(async () => {
    setLoading(true);
    setError(null);
    
    try {
      const response = await fetch(
        `${API_URL}/api/admin/marketing/history?page=${page}&page_size=${pageSize}`,
        {
          headers: {
            'Authorization': `Bearer ${localStorage.getItem('accessToken')}`
          }
        }
      );
      
      if (!response.ok) {
        const errorData = await response.json();
        throw new Error(errorData.detail || 'Failed to fetch history');
      }
      
      const data = await response.json();
      setCampaigns(data.campaigns || []);
      setTotal(data.total || 0);
      setHasMore(data.has_more || false);
    } catch (err) {
      console.error('Error fetching history:', err);
      setError(err.message);
    } finally {
      setLoading(false);
    }
  }, [page, pageSize]);

  useEffect(() => {
    fetchHistory();
  }, [fetchHistory]);

  const fetchCampaignDetail = async (campaignId) => {
    setSelectedCampaign(campaignId);
    setDetailLoading(true);
    setCampaignDetail(null);
    
    try {
      const response = await fetch(
        `${API_URL}/api/admin/marketing/history/${campaignId}`,
        {
          headers: {
            'Authorization': `Bearer ${localStorage.getItem('accessToken')}`
          }
        }
      );
      
      if (!response.ok) {
        const errorData = await response.json();
        throw new Error(errorData.detail || 'Failed to fetch campaign details');
      }
      
      const data = await response.json();
      setCampaignDetail(data);
    } catch (err) {
      console.error('Error fetching campaign detail:', err);
    } finally {
      setDetailLoading(false);
    }
  };

  const formatDate = (dateStr) => {
    if (!dateStr) return '-';
    try {
      return new Date(dateStr).toLocaleDateString('en-US', {
        month: 'short',
        day: 'numeric',
        year: 'numeric',
        hour: '2-digit',
        minute: '2-digit'
      });
    } catch {
      return dateStr;
    }
  };

  const getTemplateLabel = (templateId) => {
    const labels = {
      'welcome_back': 'Welcome Back',
      'we_miss_you': 'We Miss You',
      'getting_started': 'Getting Started',
      'custom': 'Custom'
    };
    return labels[templateId] || templateId;
  };

  const getStatusIcon = (status) => {
    switch (status) {
      case 'sent':
        return <CheckCircle2 className="w-4 h-4 text-emerald-400" />;
      case 'failed':
        return <XCircle className="w-4 h-4 text-red-400" />;
      case 'skipped_cooldown':
        return <Clock className="w-4 h-4 text-amber-400" />;
      default:
        return null;
    }
  };

  return (
    <>
      <Card className="bg-slate-900/50 border-slate-800">
        <CardContent className="p-0">
          {/* Header */}
          <div className="p-4 border-b border-slate-800">
            <div className="flex items-center gap-2">
              <History className="w-5 h-5 text-slate-400" />
              <h3 className="font-medium text-white">Campaign History</h3>
              <Badge variant="outline" className="border-slate-600 text-slate-400">
                {total} campaigns
              </Badge>
            </div>
          </div>

          {/* Loading State */}
          {loading && (
            <div className="flex justify-center items-center py-16">
              <Loader2 className="w-8 h-8 animate-spin text-blue-400" />
            </div>
          )}

          {/* Error State */}
          {error && !loading && (
            <div className="py-16 text-center">
              <AlertCircle className="w-8 h-8 text-red-400 mx-auto mb-2" />
              <p className="text-red-400">{error}</p>
              <Button
                variant="outline"
                size="sm"
                onClick={fetchHistory}
                className="mt-4 border-red-700 text-red-400 hover:bg-red-900/30"
              >
                Retry
              </Button>
            </div>
          )}

          {/* Empty State */}
          {!loading && !error && campaigns.length === 0 && (
            <div className="py-16 text-center">
              <Mail className="w-12 h-12 text-slate-600 mx-auto mb-3" />
              <p className="text-slate-400">No campaigns sent yet</p>
              <p className="text-sm text-slate-500 mt-1">
                Your outreach history will appear here
              </p>
            </div>
          )}

          {/* Campaign List */}
          {!loading && !error && campaigns.length > 0 && (
            <>
              <div className="divide-y divide-slate-800">
                {campaigns.map(campaign => (
                  <div 
                    key={campaign.campaign_id}
                    className="p-4 hover:bg-slate-800/50 cursor-pointer transition-colors"
                    onClick={() => fetchCampaignDetail(campaign.campaign_id)}
                    data-testid={`campaign-row-${campaign.campaign_id}`}
                  >
                    <div className="flex items-start justify-between">
                      <div className="flex-1 min-w-0">
                        <div className="flex items-center gap-2 mb-1">
                          <Badge variant="outline" className="border-slate-600 text-slate-300 text-xs">
                            {getTemplateLabel(campaign.template_used)}
                          </Badge>
                          <span className="text-xs text-slate-500">
                            {formatDate(campaign.sent_at)}
                          </span>
                        </div>
                        <h4 className="text-white font-medium truncate">
                          {campaign.subject}
                        </h4>
                        <div className="flex items-center gap-4 mt-2 text-sm">
                          <span className="text-slate-400 flex items-center gap-1">
                            <Users className="w-3 h-3" />
                            {campaign.recipient_count} recipients
                          </span>
                          <span className="text-emerald-400 flex items-center gap-1">
                            <CheckCircle2 className="w-3 h-3" />
                            {campaign.success_count} sent
                          </span>
                          {campaign.failed_count > 0 && (
                            <span className="text-red-400 flex items-center gap-1">
                              <XCircle className="w-3 h-3" />
                              {campaign.failed_count} failed
                            </span>
                          )}
                        </div>
                        <p className="text-xs text-slate-500 mt-1">
                          by {campaign.sent_by_name || campaign.sent_by_email}
                        </p>
                      </div>
                      <ChevronRight className="w-5 h-5 text-slate-500 flex-shrink-0" />
                    </div>
                  </div>
                ))}
              </div>

              {/* Pagination */}
              <div className="flex items-center justify-between px-4 py-3 border-t border-slate-800">
                <span className="text-sm text-slate-400">
                  Page {page} of {Math.ceil(total / pageSize)}
                </span>
                <div className="flex gap-2">
                  <Button
                    variant="outline"
                    size="sm"
                    onClick={() => setPage(p => p - 1)}
                    disabled={page === 1}
                    className="border-slate-700 text-slate-300 hover:bg-slate-800"
                  >
                    Previous
                  </Button>
                  <Button
                    variant="outline"
                    size="sm"
                    onClick={() => setPage(p => p + 1)}
                    disabled={!hasMore}
                    className="border-slate-700 text-slate-300 hover:bg-slate-800"
                  >
                    Next
                  </Button>
                </div>
              </div>
            </>
          )}
        </CardContent>
      </Card>

      {/* Campaign Detail Modal */}
      <Dialog open={!!selectedCampaign} onOpenChange={() => setSelectedCampaign(null)}>
        <DialogContent className="max-w-3xl bg-slate-900 border-slate-800 text-white max-h-[90vh] overflow-y-auto">
          <DialogHeader>
            <DialogTitle className="text-xl font-bold">Campaign Details</DialogTitle>
          </DialogHeader>

          {detailLoading && (
            <div className="flex justify-center items-center py-16">
              <Loader2 className="w-8 h-8 animate-spin text-blue-400" />
            </div>
          )}

          {campaignDetail && (
            <div className="space-y-6 mt-4">
              {/* Campaign Info */}
              <div className="grid grid-cols-2 gap-4">
                <div>
                  <span className="text-xs text-slate-500">Sent At</span>
                  <p className="text-white">{formatDate(campaignDetail.sent_at)}</p>
                </div>
                <div>
                  <span className="text-xs text-slate-500">Sent By</span>
                  <p className="text-white">{campaignDetail.sent_by_name || campaignDetail.sent_by_email}</p>
                </div>
                <div>
                  <span className="text-xs text-slate-500">Template</span>
                  <p className="text-white">{getTemplateLabel(campaignDetail.template_used)}</p>
                </div>
                <div>
                  <span className="text-xs text-slate-500">Segment</span>
                  <p className="text-white capitalize">{campaignDetail.segment_targeted.replace(/_/g, ' ')}</p>
                </div>
              </div>

              {/* Subject & Body */}
              <div className="space-y-2">
                <span className="text-xs text-slate-500">Subject</span>
                <p className="text-white bg-slate-800 p-3 rounded">{campaignDetail.subject}</p>
              </div>
              <div className="space-y-2">
                <span className="text-xs text-slate-500">Body</span>
                <p className="text-slate-300 bg-slate-800 p-3 rounded whitespace-pre-wrap text-sm">
                  {campaignDetail.body}
                </p>
              </div>

              {/* Stats */}
              <div className="flex gap-4 p-3 bg-slate-800/50 rounded">
                <div className="text-center">
                  <span className="text-2xl font-bold text-white">{campaignDetail.total_recipients}</span>
                  <p className="text-xs text-slate-400">Total</p>
                </div>
                <div className="text-center">
                  <span className="text-2xl font-bold text-emerald-400">{campaignDetail.success_count}</span>
                  <p className="text-xs text-slate-400">Sent</p>
                </div>
                <div className="text-center">
                  <span className="text-2xl font-bold text-red-400">{campaignDetail.failed_count}</span>
                  <p className="text-xs text-slate-400">Failed</p>
                </div>
                <div className="text-center">
                  <span className="text-2xl font-bold text-amber-400">{campaignDetail.skipped_count}</span>
                  <p className="text-xs text-slate-400">Skipped</p>
                </div>
              </div>

              {/* Recipients Table */}
              <div>
                <h4 className="font-medium text-white mb-3">Recipients ({campaignDetail.recipients.length})</h4>
                <div className="border border-slate-800 rounded overflow-hidden">
                  <Table>
                    <TableHeader>
                      <TableRow className="border-slate-800 hover:bg-transparent">
                        <TableHead className="text-slate-300">Email</TableHead>
                        <TableHead className="text-slate-300">Status</TableHead>
                        <TableHead className="text-slate-300">Details</TableHead>
                      </TableRow>
                    </TableHeader>
                    <TableBody>
                      {campaignDetail.recipients.map((recipient, idx) => (
                        <TableRow key={idx} className="border-slate-800">
                          <TableCell className="text-white">{recipient.email}</TableCell>
                          <TableCell>
                            <div className="flex items-center gap-2">
                              {getStatusIcon(recipient.status)}
                              <span className={
                                recipient.status === 'sent' ? 'text-emerald-400' :
                                recipient.status === 'failed' ? 'text-red-400' :
                                'text-amber-400'
                              }>
                                {recipient.status === 'skipped_cooldown' ? 'Skipped (Cooldown)' : 
                                 recipient.status.charAt(0).toUpperCase() + recipient.status.slice(1)}
                              </span>
                            </div>
                          </TableCell>
                          <TableCell className="text-slate-400 text-sm">
                            {recipient.sent_at ? formatDate(recipient.sent_at) : recipient.error || '-'}
                          </TableCell>
                        </TableRow>
                      ))}
                    </TableBody>
                  </Table>
                </div>
              </div>
            </div>
          )}
        </DialogContent>
      </Dialog>
    </>
  );
};

export default OutreachHistory;
